import { type ReactNode, useCallback } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Icon_Checkmark24, Menu } from "@shapr3d/frontend-lib";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import useTranslation from "../../../../../hooks/useTranslation";
import { DoubleIconLabel } from "./DoubleIconLabel";
import { Icon_FillerSvg24 } from "./FillerSvg";
import { MenuPortal } from "./MenuPortal";
import { SelectActivator } from "./SelectActivator";
import { SingleIconLabel } from "./SingleIconLabel";

import style from "./GeneralAccess.module.css";

interface GeneralAccessProps<
  TAccess extends readonly [string, ...string[]],
  TRoles extends readonly [string, ...string[]],
> {
  accessOptions: TAccess;
  roles: TRoles;
  initialGeneralAccess?: TAccess[number];
  initialBaseRole?: TRoles[number];

  accessDisplayName: (access: TAccess[number]) => string;
  roleDisplayName: (role: TRoles[number]) => string;

  getAccessIcon: (access: TAccess[number]) => ReactNode;

  isDisabled?: boolean;

  onSubmit: (generalAccess: TAccess[number], baseRole: TRoles[number]) => Promise<unknown>;
  onGeneralAccessChange?: (newValue: TAccess[number], oldValue: TAccess[number]) => void;
  onBaseRoleChange?: (newValue: TRoles[number], oldValue: TRoles[number]) => void;

  restrictedAccessValue?: TAccess[number];
}

export const GeneralAccess = <
  TAccess extends readonly [string, ...string[]],
  TRoles extends readonly [string, ...string[]],
>({
  accessOptions,
  roles,
  initialGeneralAccess,
  initialBaseRole,
  isDisabled = false,
  onSubmit,
  onGeneralAccessChange,
  onBaseRoleChange,
  accessDisplayName,
  roleDisplayName,
  getAccessIcon,
  restrictedAccessValue,
}: GeneralAccessProps<TAccess, TRoles>) => {
  const { t, m } = useTranslation();
  const createSchema = <
    TAccess extends readonly [string, ...string[]],
    TRoles extends readonly [string, ...string[]],
  >(
    accessOptions: TAccess,
    roles: TRoles
  ) =>
    z.object({
      generalAccess: z.enum(accessOptions),
      baseRole: z.enum(roles),
    });

  type GeneralAccessFormValues = {
    generalAccess: string;
    baseRole: string;
  };

  const { setValue, setError, control, formState } = useForm<GeneralAccessFormValues>({
    mode: "onChange",
    defaultValues: {
      generalAccess: initialGeneralAccess,
      baseRole: initialBaseRole,
    },
    resolver: zodResolver(createSchema<TAccess, TRoles>(accessOptions, roles)),
  });

  const generalAccessValue = useWatch({ name: "generalAccess", control });
  const baseRoleValue = useWatch({ name: "baseRole", control });

  const handleGeneralAccessChange = useCallback(
    async (newAccess: TAccess[number]) => {
      if (generalAccessValue === newAccess) {
        return;
      }

      const oldAccess = generalAccessValue;
      setValue("generalAccess", newAccess);
      onGeneralAccessChange?.(newAccess, generalAccessValue);
      try {
        await onSubmit(newAccess, baseRoleValue);
      } catch {
        setError("generalAccess", { message: t(m.ErrorMessage.Unknown) });
        setValue("generalAccess", oldAccess);
      }
    },
    [generalAccessValue, onGeneralAccessChange, setValue, onSubmit, baseRoleValue, setError, t, m]
  );

  const handleBaseRoleChange = useCallback(
    async (newRole: TRoles[number]) => {
      if (baseRoleValue === newRole) {
        return;
      }
      const oldRole = baseRoleValue;
      setValue("baseRole", newRole);
      onBaseRoleChange?.(newRole, baseRoleValue);
      try {
        await onSubmit(generalAccessValue, newRole);
      } catch {
        setError("generalAccess", { message: t(m.ErrorMessage.Unknown) });
        setValue("generalAccess", oldRole);
      }
    },
    [baseRoleValue, onBaseRoleChange, setValue, onSubmit, generalAccessValue, setError, t, m]
  );

  return (
    <div className={style.generalAccessContainer}>
      <div>
        <MenuPortal
          dropdownPosition="right"
          activator={
            <SelectActivator
              aria-label={t(m.ShareModal.GeneralAccessAriaLabel)}
              icon={getAccessIcon(generalAccessValue)}
              label={accessDisplayName(generalAccessValue)}
              disabled={isDisabled}
              className={style.bold}
            />
          }
        >
          {accessOptions.map((access) => (
            <Menu.Item
              key={access}
              closeOnClick
              onClick={(event) => {
                event.preventDefault();
                handleGeneralAccessChange(access);
              }}
            >
              <DoubleIconLabel
                icon1={generalAccessValue === access ? <Icon_Checkmark24 /> : null}
                icon2={getAccessIcon(access)}
                label={accessDisplayName(access)}
              />
            </Menu.Item>
          ))}
        </MenuPortal>
        {formState.errors.generalAccess?.message && (
          <span className={style.errorMessage}>{formState.errors.generalAccess.message}</span>
        )}
      </div>

      {restrictedAccessValue && generalAccessValue !== restrictedAccessValue && (
        <div>
          <MenuPortal
            dropdownPosition="left"
            activator={
              <SelectActivator
                aria-label={t(m.ShareModal.BaseRoleAriaLabel)}
                label={roleDisplayName(baseRoleValue)}
                disabled={isDisabled}
              />
            }
          >
            {roles.map((role) => (
              <Menu.Item
                key={role}
                closeOnClick
                onClick={(event) => {
                  event.preventDefault();
                  handleBaseRoleChange(role);
                }}
              >
                <SingleIconLabel
                  icon={baseRoleValue === role ? <Icon_Checkmark24 /> : <Icon_FillerSvg24 />}
                  label={roleDisplayName(role)}
                />
              </Menu.Item>
            ))}
          </MenuPortal>
        </div>
      )}
    </div>
  );
};
